import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/lib/language";
import { testimonialApi } from "@/lib/api";
import { Testimonial } from "@/lib/types";
import {
  Star,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function TestimonialManagement() {
  const { t } = useLanguage();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await testimonialApi.getAllTestimonials();
      setTestimonials(data);
    } catch (err) {
      console.error("Error loading testimonials:", err);
      setError(t("errorLoadingContacts"));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    id: string,
    status: "approved" | "rejected"
  ) => {
    try {
      setActionLoading(id);
      await testimonialApi.updateTestimonialStatus(id, status);
      await loadTestimonials(); // Reload data
    } catch (err) {
      console.error("Error updating testimonial status:", err);
      setError(t("failedToUpdateTestimonial"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setActionLoading(id);
      await testimonialApi.deleteTestimonial(id);
      await loadTestimonials(); // Reload data
    } catch (err) {
      console.error("Error deleting testimonial:", err);
      setError(t("failedToDeleteTestimonial"));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            Pending
          </Badge>
        );
    }
  };

  const pendingTestimonials = testimonials.filter(
    (t) => t.status === "pending"
  );
  const approvedTestimonials = testimonials.filter(
    (t) => t.status === "approved"
  );
  const rejectedTestimonials = testimonials.filter(
    (t) => t.status === "rejected"
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-32 bg-gray-200 rounded animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h2 className="text-2xl font-bold">{t("manageTestimonials")}</h2>
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          <span>
            {t("pending")}: {pendingTestimonials.length}
          </span>
          <span>
            {t("approved")}: {approvedTestimonials.length}
          </span>
          <span>
            {t("rejected")}: {rejectedTestimonials.length}
          </span>
        </div>
      </div>

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Pending Testimonials */}
      {pendingTestimonials.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              {t("pendingTestimonials")} ({pendingTestimonials.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingTestimonials.map((testimonial) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                onApprove={() => handleStatusUpdate(testimonial.id, "approved")}
                onReject={() => handleStatusUpdate(testimonial.id, "rejected")}
                onDelete={() => handleDelete(testimonial.id)}
                isLoading={actionLoading === testimonial.id}
                showActions={true}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Approved Testimonials */}
      {approvedTestimonials.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              {t("approvedTestimonials")} ({approvedTestimonials.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {approvedTestimonials.map((testimonial) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                onReject={() => handleStatusUpdate(testimonial.id, "rejected")}
                onDelete={() => handleDelete(testimonial.id)}
                isLoading={actionLoading === testimonial.id}
                showActions={true}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Rejected Testimonials */}
      {rejectedTestimonials.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Rejected Testimonials ({rejectedTestimonials.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {rejectedTestimonials.map((testimonial) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                onApprove={() => handleStatusUpdate(testimonial.id, "approved")}
                onDelete={() => handleDelete(testimonial.id)}
                isLoading={actionLoading === testimonial.id}
                showActions={true}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {testimonials.length === 0 && !loading && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t("noTestimonials")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface TestimonialCardProps {
  testimonial: Testimonial;
  onApprove?: () => void;
  onReject?: () => void;
  onDelete?: () => void;
  isLoading?: boolean;
  showActions?: boolean;
}

function TestimonialCard({
  testimonial,
  onApprove,
  onReject,
  onDelete,
  isLoading = false,
  showActions = false,
}: TestimonialCardProps) {
  const { t } = useLanguage();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            Pending
          </Badge>
        );
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">{testimonial.name}</h4>
            {getStatusBadge(testimonial.status)}
          </div>
          <p className="text-sm text-muted-foreground">{testimonial.email}</p>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={14}
                className={
                  star <= testimonial.rating
                    ? "text-amber-400 fill-current"
                    : "text-gray-300"
                }
              />
            ))}
            <span className="ml-1 text-sm text-muted-foreground">
              ({testimonial.rating}/5)
            </span>
          </div>
        </div>
        <div className="text-sm text-muted-foreground self-start sm:text-right">
          {formatDate(testimonial.createdAt)}
        </div>
      </div>

      <p className="text-sm leading-relaxed italic">"{testimonial.message}"</p>

      {showActions && (
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {testimonial.status === "pending" && onApprove && (
            <Button
              size="sm"
              onClick={onApprove}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              {t("approve")}
            </Button>
          )}

          {testimonial.status === "approved" && onReject && (
            <Button
              size="sm"
              variant="outline"
              onClick={onReject}
              disabled={isLoading}
            >
              <XCircle className="h-4 w-4 mr-1" />
              {t("reject")}
            </Button>
          )}

          {testimonial.status === "rejected" && onApprove && (
            <Button size="sm" onClick={onApprove} disabled={isLoading}>
              <CheckCircle className="h-4 w-4 mr-1" />
              {t("approve")}
            </Button>
          )}

          {onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isLoading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {t("delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Testimonial</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this testimonial? This
                    action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}
    </div>
  );
}
